import {
    AdminDevice,
    listAdminDevices,
    migrateAdminDevices,
    MigrateSummary,
} from '../../data/AdminDevice';
import { apiErrorMessage } from '../../data/apiClient';
import { listAdminOrganizations } from '../../data/Organization';
import { useCsrf } from '../../hooks/useCsrf';
import { usePermissions } from '../../hooks/usePermissions';
import {
    ActionIcon,
    Badge,
    Button,
    Checkbox,
    Code,
    Drawer,
    Group,
    Loader,
    Menu,
    Pagination,
    Select,
    Stack,
    Table,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowsExchange, IconChevronDown } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

const PAGE_SIZE = 50;

const strategyLabel = (strategy: MigrateSummary['contentStrategy']): string => {
    switch (strategy) {
        case 'moved':
            return 'Moved exclusive playlist → scene → component graph (same IDs, new org)';
        case 'cloned':
            return 'Cloned shared playlist → scene → component graph into the destination org';
        default:
            return 'No playlist content graph to transfer';
    }
};

/**
 * Platform-wide device administration. Watchly super admins only.
 */
const AdminDevicesPage = () => {
    const { showAdminTools, isSuperAdmin, isLoading: authLoading } = usePermissions();
    const { csrfToken } = useCsrf();

    const [page, setPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [migratingDevices, setMigratingDevices] = useState<AdminDevice[]>([]);
    /** Bulk always shows a device table; row action keeps the single-device summary. */
    const [migratePresentation, setMigratePresentation] = useState<'single' | 'table'>('single');
    const [destOrgId, setDestOrgId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [summary, setSummary] = useState<MigrateSummary | null>(null);
    // Guard against double-clicks before React re-renders `submitting` — two
    // concurrent migrate requests each clone shared locations/tags separately.
    const migrateInFlight = useRef(false);

    const listKey = showAdminTools
        ? `api/admin/devices?page=${page}&page_size=${PAGE_SIZE}`
        : null;

    const {
        data: pageData,
        error,
        isLoading,
        mutate,
    } = useSWR(listKey, listAdminDevices);

    const devices = pageData?.data ?? [];
    const totalPages = pageData?.lastPage ?? 1;

    // Drop selections that are no longer on the current page (e.g. after migrate/refresh).
    useEffect(() => {
        const visible = new Set(devices.map(d => d.id));
        setSelectedIds(prev => {
            const next = new Set(Array.from(prev).filter(id => visible.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [devices]);

    const { data: orgs } = useSWR(
        migratingDevices.length > 0 && showAdminTools ? 'api/admin/organizations' : null,
        listAdminOrganizations,
    );

    const orgOptions = useMemo(() => {
        const currentOrgIds = new Set(
            migratingDevices.map(d => d.orgId).filter((id): id is string => id !== null),
        );
        // Only exclude an org when every selected device already belongs to it
        // (otherwise the migrator would reject "same org" for that subset).
        const sharedOrgId =
            currentOrgIds.size === 1 ? Array.from(currentOrgIds)[0] : null;
        const sharedCurrentOrgId =
            sharedOrgId !== null && migratingDevices.every(d => d.orgId === sharedOrgId)
                ? sharedOrgId
                : null;

        return (orgs ?? [])
            .filter(org => org.id !== sharedCurrentOrgId)
            .map(org => ({ value: org.id, label: org.name }));
    }, [orgs, migratingDevices]);

    const allPageSelected =
        devices.length > 0 && devices.every(device => selectedIds.has(device.id));
    const somePageSelected = devices.some(device => selectedIds.has(device.id));

    const selectedDevices = useMemo(
        () => devices.filter(device => selectedIds.has(device.id)),
        [devices, selectedIds],
    );

    const toggleAllOnPage = (checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) {
                devices.forEach(device => next.add(device.id));
            } else {
                devices.forEach(device => next.delete(device.id));
            }
            return next;
        });
    };

    const toggleOne = (deviceId: string, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) {
                next.add(deviceId);
            } else {
                next.delete(deviceId);
            }
            return next;
        });
    };

    const openMigrate = (devicesToMigrate: AdminDevice[], presentation: 'single' | 'table') => {
        setSummary(null);
        setDestOrgId(null);
        setMigratePresentation(presentation);
        setMigratingDevices(devicesToMigrate);
    };

    const closeDrawer = () => {
        setMigratingDevices([]);
        setDestOrgId(null);
        setSummary(null);
        setSubmitting(false);
    };

    const finishAndRefresh = async () => {
        setSelectedIds(new Set());
        closeDrawer();
        await mutate();
    };

    const handleMigrate = async () => {
        if (migratingDevices.length === 0 || !destOrgId || migrateInFlight.current) {
            return;
        }

        migrateInFlight.current = true;
        setSubmitting(true);
        try {
            const result = await migrateAdminDevices(
                csrfToken,
                migratingDevices.map(d => d.id),
                destOrgId,
            );
            setSummary(result.summary);
        } catch (err) {
            notifications.show({
                title: 'Migration failed',
                message: apiErrorMessage(err, 'Could not migrate the selected devices.'),
                color: 'red',
            });
        } finally {
            migrateInFlight.current = false;
            setSubmitting(false);
        }
    };

    if (authLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    if (!showAdminTools) {
        return (
            <Stack px="md">
                <Title order={3}>Devices</Title>
                <Text c="dimmed">
                    {isSuperAdmin
                        ? 'Exit the customer view to manage platform devices.'
                        : 'You don’t have permission to view platform devices.'}
                </Text>
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack px="md">
                <Title order={3}>Devices</Title>
                <Text c="red">
                    Error: {error instanceof AxiosError ? error.message : 'Failed to load devices'}
                </Text>
            </Stack>
        );
    }

    return (
        <Stack px="md" gap="lg">
            <Group justify="space-between" align="flex-end">
                <div>
                    <Title order={3}>Devices</Title>
                    <Text size="sm" c="dimmed">
                        Every device, claimed and unclaimed, across all Organizations.
                    </Text>
                </div>

                {selectedIds.size > 0 && (
                    <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                            <Button
                                variant="default"
                                rightSection={<IconChevronDown size={16} />}
                            >
                                Bulk actions ({selectedIds.size})
                            </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                leftSection={<IconArrowsExchange size={16} />}
                                onClick={() => openMigrate(selectedDevices, 'table')}
                            >
                                Migrate to a new Org
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                )}
            </Group>

            {isLoading ? (
                <Group justify="center" py="xl">
                    <Loader />
                </Group>
            ) : devices.length === 0 ? (
                <Text c="dimmed">No devices found.</Text>
            ) : (
                <>
                    <Table.ScrollContainer minWidth={960}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={40}>
                                        <Checkbox
                                            aria-label="Select all devices on this page"
                                            checked={allPageSelected}
                                            indeterminate={somePageSelected && !allPageSelected}
                                            onChange={event =>
                                                toggleAllOnPage(event.currentTarget.checked)
                                            }
                                        />
                                    </Table.Th>
                                    <Table.Th>Device ID</Table.Th>
                                    <Table.Th>Device Name</Table.Th>
                                    <Table.Th>Org Name</Table.Th>
                                    <Table.Th>Org ID</Table.Th>
                                    <Table.Th>Tags</Table.Th>
                                    <Table.Th>Location</Table.Th>
                                    <Table.Th w={72}>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {devices.map(device => (
                                    <Table.Tr key={device.id}>
                                        <Table.Td>
                                            <Checkbox
                                                aria-label={`Select ${device.name ?? device.id}`}
                                                checked={selectedIds.has(device.id)}
                                                onChange={event =>
                                                    toggleOne(
                                                        device.id,
                                                        event.currentTarget.checked,
                                                    )
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Code style={{ fontSize: 11 }}>{device.id}</Code>
                                        </Table.Td>
                                        <Table.Td>{device.name ?? '—'}</Table.Td>
                                        <Table.Td>
                                            {device.org ? (
                                                device.org.name
                                            ) : (
                                                <Text span c="dimmed" fs="italic">
                                                    Unclaimed
                                                </Text>
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            {device.orgId ? (
                                                <Code style={{ fontSize: 11 }}>{device.orgId}</Code>
                                            ) : (
                                                '—'
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Group gap={4}>
                                                {device.deviceTags.length === 0
                                                    ? '—'
                                                    : device.deviceTags.map(tag => (
                                                          <Badge
                                                              key={tag.id}
                                                              size="sm"
                                                              variant="outline"
                                                          >
                                                              {tag.label}
                                                          </Badge>
                                                      ))}
                                            </Group>
                                        </Table.Td>
                                        <Table.Td>{device.location?.name ?? '—'}</Table.Td>
                                        <Table.Td>
                                            <Group gap={4} justify="flex-start">
                                                <Tooltip label="Migrate to a new Org">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="gray"
                                                        aria-label="Migrate to a new Org"
                                                        onClick={() =>
                                                            openMigrate([device], 'single')
                                                        }
                                                    >
                                                        <IconArrowsExchange size={18} stroke={1.5} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    {totalPages > 1 && (
                        <Group justify="center">
                            <Pagination value={page} onChange={setPage} total={totalPages} />
                        </Group>
                    )}
                </>
            )}

            <Drawer
                opened={migratingDevices.length > 0}
                onClose={summary ? finishAndRefresh : closeDrawer}
                position="right"
                size={migratePresentation === 'table' ? 'lg' : 'md'}
                padding="lg"
                title={summary ? 'Migration complete' : 'Migrate to a new Org'}
                overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
            >
                {migratingDevices.length > 0 && summary === null && (
                    <Stack gap="md">
                        {migratePresentation === 'single' && migratingDevices[0] ? (
                            <>
                                <div>
                                    <Text size="sm" c="dimmed">
                                        Device name
                                    </Text>
                                    <Text fw={500}>{migratingDevices[0].name ?? '—'}</Text>
                                </div>
                                <div>
                                    <Text size="sm" c="dimmed">
                                        Device ID
                                    </Text>
                                    <Code>{migratingDevices[0].id}</Code>
                                </div>
                                <div>
                                    <Text size="sm" c="dimmed">
                                        Current organization
                                    </Text>
                                    {migratingDevices[0].org ? (
                                        <Stack gap={2}>
                                            <Text fw={500}>{migratingDevices[0].org.name}</Text>
                                            <Code style={{ fontSize: 11 }}>
                                                {migratingDevices[0].org.id}
                                            </Code>
                                        </Stack>
                                    ) : (
                                        <Text fs="italic" c="dimmed">
                                            Unclaimed
                                        </Text>
                                    )}
                                </div>
                            </>
                        ) : (
                            <Table.ScrollContainer minWidth={420}>
                                <Table striped withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Device</Table.Th>
                                            <Table.Th>Organization</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {migratingDevices.map(device => (
                                            <Table.Tr key={device.id}>
                                                <Table.Td>
                                                    <Stack gap={2}>
                                                        <Text size="sm" fw={500}>
                                                            {device.name ?? '—'}
                                                        </Text>
                                                        <Code style={{ fontSize: 11 }}>
                                                            {device.id}
                                                        </Code>
                                                    </Stack>
                                                </Table.Td>
                                                <Table.Td>
                                                    {device.org ? (
                                                        <Stack gap={2}>
                                                            <Text size="sm">{device.org.name}</Text>
                                                            <Code style={{ fontSize: 11 }}>
                                                                {device.org.id}
                                                            </Code>
                                                        </Stack>
                                                    ) : (
                                                        <Text size="sm" fs="italic" c="dimmed">
                                                            Unclaimed
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        )}

                        <Stack gap="xs">
                            <Text size="sm" c="dimmed">
                                Locations and device tags on these devices are always cloned into
                                the destination organization. After the move, if a location or tag
                                has no devices left in the source organization, the emptied original
                                is deleted.
                            </Text>
                            <Text size="sm" c="dimmed">
                                Playlists, scenes, and components are moved into the destination
                                organization when they are used only by the devices being migrated.
                                If any of that content is still shared with devices or content
                                remaining in the source organization, it is cloned instead so the
                                source org keeps its copy.
                            </Text>
                        </Stack>

                        <Select
                            label="Destination organization"
                            placeholder="Start typing an organization name…"
                            searchable
                            nothingFoundMessage="No matching organizations"
                            data={orgOptions}
                            value={destOrgId}
                            onChange={setDestOrgId}
                            disabled={submitting}
                        />

                        <Group justify="flex-end" mt="md">
                            <Button variant="default" onClick={closeDrawer} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleMigrate}
                                disabled={!destOrgId}
                                loading={submitting}
                            >
                                Migrate
                                {migratingDevices.length > 1
                                    ? ` (${migratingDevices.length})`
                                    : ''}
                            </Button>
                        </Group>
                    </Stack>
                )}

                {summary !== null && (
                    <Stack gap="md">
                        <Text size="sm">{strategyLabel(summary.contentStrategy)}</Text>

                        <Table withTableBorder withColumnBorders>
                            <Table.Tbody>
                                {(
                                    [
                                        ['Devices moved', summary.devices],
                                        ['Locations cloned', summary.locationsCloned],
                                        ['Device tags cloned', summary.deviceTagsCloned],
                                        ['Playlists', summary.playlists],
                                        ['Scenes', summary.scenes],
                                        ['Components', summary.components],
                                        [
                                            'Locations deleted (emptied by move)',
                                            summary.locationsDeleted,
                                        ],
                                        [
                                            'Device tags deleted (emptied by move)',
                                            summary.deviceTagsDeleted,
                                        ],
                                        ['Device configs following', summary.deviceConfigs],
                                        ['Device pings following', summary.devicePings],
                                    ] as const
                                ).map(([label, count]) => (
                                    <Table.Tr key={label}>
                                        <Table.Td>{label}</Table.Td>
                                        <Table.Td w={72} ta="right">
                                            {count}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>

                        <Group justify="flex-end" mt="md">
                            <Button onClick={finishAndRefresh}>Done</Button>
                        </Group>
                    </Stack>
                )}
            </Drawer>
        </Stack>
    );
};

export { AdminDevicesPage };
